import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';

export default function LimitModal({
  isOpen,
  onClose,
  type = 'compliance', // 'compliance' | 'internal' | 'alert'
  meters = [],
  selectedMeter = '',
  existingLimit = 0,
  existingNocDetails = {},
  existingAlertLimit = 0,
  existingAlertEmail = '',
  existingAlertFrequency = 'daily',
  unit = 'm³',
  onSave,
}) {
  const isCompliance = type === 'compliance';
  const isAlert = type === 'alert';

  const title = isAlert
    ? 'Set Consumption Alert Limit'
    : isCompliance
    ? 'Set Compliance Limit & NOC'
    : 'Set Internal Limit';

  const subtitle = isAlert
    ? 'Configure automated email notifications when consumption exceeds daily, weekly, or monthly limits'
    : isCompliance
    ? 'Configure daily/yearly extraction limits and upload official CGWA / Ground Water NOC'
    : 'Set Internal limit for operational water consumption tracking';

  const [meterId, setMeterId] = useState(selectedMeter);

  // Compliance & Internal limit states
  const [dailyLimit, setDailyLimit] = useState('');
  const [yearlyLimit, setYearlyLimit] = useState('');
  const [nocNumber, setNocNumber] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState('');

  // Alert limit states
  const [alertLimit, setAlertLimit] = useState('');
  const [alertEmail, setAlertEmail] = useState('');
  const [alertFrequency, setAlertFrequency] = useState('daily');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      const activeMid = selectedMeter && selectedMeter !== 'All' ? selectedMeter : meters[0]?.value || '';
      setMeterId(activeMid);

      if (isAlert) {
        const aLim = existingAlertLimit > 0 ? existingAlertLimit : existingLimit > 0 ? existingLimit : '';
        setAlertLimit(aLim ? String(aLim) : '');
        setAlertEmail(existingAlertEmail || '');
        setAlertFrequency(existingAlertFrequency || 'daily');
      } else {
        const dLim =
          existingNocDetails?.dailyLimit !== undefined && existingNocDetails?.dailyLimit !== null
            ? existingNocDetails.dailyLimit
            : existingLimit > 0
            ? existingLimit
            : '';
        setDailyLimit(dLim > 0 ? String(dLim) : '');

        setYearlyLimit(
          existingNocDetails?.yearlyLimit !== undefined && existingNocDetails?.yearlyLimit !== null && existingNocDetails.yearlyLimit > 0
            ? String(existingNocDetails.yearlyLimit)
            : ''
        );

        setNocNumber(existingNocDetails?.nocNumber || '');

        setStartDate(
          existingNocDetails?.startDate ? moment(existingNocDetails.startDate).format('YYYY-MM-DD') : ''
        );

        setEndDate(
          existingNocDetails?.endDate ? moment(existingNocDetails.endDate).format('YYYY-MM-DD') : ''
        );

        setSelectedFile(null);
        setFileError('');
      }
    }
  }, [
    isOpen,
    isAlert,
    selectedMeter,
    existingLimit,
    existingNocDetails,
    existingAlertLimit,
    existingAlertEmail,
    existingAlertFrequency,
    meters,
  ]);

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf') && file.type !== 'application/pdf') {
      setFileError('Please select a valid PDF file.');
      setSelectedFile(null);
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      setFileError('File size must be under 15MB.');
      setSelectedFile(null);
      return;
    }
    setFileError('');
    setSelectedFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isAlert) {
      const numAlert = parseFloat(alertLimit);
      if (isNaN(numAlert) || numAlert < 0) {
        alert('Please enter a valid positive number for the alert limit.');
        return;
      }
      if (numAlert > 0 && (!alertEmail || !alertEmail.includes('@'))) {
        alert('Please enter a valid recipient email address.');
        return;
      }

      setIsSubmitting(true);
      try {
        if (onSave) {
          await onSave({
            meterId: meterId || selectedMeter,
            alertLimit: numAlert,
            alertEmail: alertEmail.trim(),
            alertFrequency: alertFrequency || 'daily',
            type: 'alert',
          });
        }
        onClose();
      } catch (err) {
        console.error('Error saving alert limit:', err);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    const numDaily = parseFloat(dailyLimit);
    if (isNaN(numDaily) || numDaily < 0) {
      alert('Please enter a valid positive number for the limit.');
      return;
    }

    setIsSubmitting(true);
    try {
      if (onSave) {
        if (isCompliance) {
          await onSave({
            meterId: meterId || selectedMeter,
            dailyLimit: numDaily,
            yearlyLimit: yearlyLimit ? parseFloat(yearlyLimit) : 0,
            nocNumber: nocNumber.trim(),
            startDate: startDate || null,
            endDate: endDate || null,
            nocFile: selectedFile,
            type: 'compliance',
          });
        } else {
          await onSave({
            meterId: meterId || selectedMeter,
            dailyLimit: numDaily,
            type: 'internal',
          });
        }
      }
      onClose();
    } catch (err) {
      console.error('Error saving limit:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const matchedMeterObj = meters.find((m) => (m.value || m.productId) === meterId) || {};
  const meterDisplayName = matchedMeterObj.label || `${meterId} · ${matchedMeterObj.name || 'Borewell'}`;
  const isSingleMeterActive = Boolean(selectedMeter && selectedMeter !== 'All');

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(11, 46, 69, 0.55)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        animation: 'fade .2s ease',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSubmitting) onClose();
      }}
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: '16px',
          boxShadow: '0 24px 50px -12px rgba(11, 46, 69, 0.35)',
          width: '100%',
          maxWidth: isCompliance ? '640px' : isAlert ? '540px' : '520px',
          padding: '26px 30px 28px',
          position: 'relative',
          maxHeight: '92vh',
          overflowY: 'auto',
          animation: 'pop .2s ease',
        }}
      >
        {/* Close Icon (X) */}
        <button
          onClick={onClose}
          disabled={isSubmitting}
          style={{
            position: 'absolute',
            top: '18px',
            right: '18px',
            border: 0,
            background: 'none',
            cursor: 'pointer',
            padding: '6px',
            color: '#0b2e45',
            display: 'grid',
            placeItems: 'center',
            borderRadius: '6px',
            transition: 'background .15s',
          }}
          title="Close modal"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Modal Header */}
        <div style={{ marginBottom: '18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            {isAlert && (
              <span style={{ fontSize: '18px' }}>🔔</span>
            )}
            <h2
              style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: 800,
                color: '#0d3b5e',
                letterSpacing: '-.01em',
              }}
            >
              {title}
            </h2>
          </div>
          <p
            style={{
              margin: 0,
              fontSize: '13px',
              color: '#6b7280',
            }}
          >
            {subtitle}
          </p>
        </div>

        {/* Target Meter Display */}
        <div
          style={{
            marginBottom: '18px',
            padding: '10px 14px',
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span style={{ fontSize: '12.5px', color: '#4b5563', fontWeight: 600 }}>Target Meter:</span>
          {isSingleMeterActive ? (
            <span style={{ fontSize: '13.5px', fontWeight: 750, color: '#0f766e', background: '#ecfdf5', padding: '3px 10px', borderRadius: '6px' }}>
              {meterDisplayName}
            </span>
          ) : (
            <select
              value={meterId}
              onChange={(e) => setMeterId(e.target.value)}
              style={{
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                padding: '4px 10px',
                fontSize: '13px',
                fontWeight: 600,
                color: '#0d3b5e',
                background: '#fff',
                cursor: 'pointer',
              }}
            >
              {meters.filter((m) => m.value && m.value !== 'All').map((m) => (
                <option key={m.value || m.productId} value={m.value || m.productId}>
                  {m.label || m.value}
                </option>
              ))}
            </select>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          {isAlert ? (
            /* Alert Limit Configuration Form */
            <>
              {/* Frequency Selector */}
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '12.5px', fontWeight: 700, color: '#334155' }}>
                  Alert Frequency / Period <span style={{ color: '#e53e3e' }}>*</span>
                </label>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: '8px',
                    background: '#f1f5f9',
                    padding: '4px',
                    borderRadius: '10px',
                  }}
                >
                  {[
                    { id: 'daily', label: 'Daily', icon: '☀️', desc: "Today's usage (00:00 to now)" },
                    { id: 'weekly', label: 'Weekly', icon: '📅', desc: 'Current week usage (Mon to now)' },
                    { id: 'monthly', label: 'Monthly', icon: '📊', desc: 'Current month usage (1st to now)' },
                  ].map((f) => {
                    const active = alertFrequency === f.id;
                    return (
                      <button
                        key={f.id}
                        type="button"
                        onClick={() => setAlertFrequency(f.id)}
                        style={{
                          padding: '10px 8px',
                          border: active ? '1.5px solid #0284c7' : '1px solid transparent',
                          borderRadius: '8px',
                          background: active ? '#ffffff' : 'transparent',
                          color: active ? '#0369a1' : '#64748b',
                          fontWeight: active ? 800 : 600,
                          fontSize: '13px',
                          cursor: 'pointer',
                          boxShadow: active ? '0 2px 6px rgba(0, 0, 0, 0.08)' : 'none',
                          transition: 'all .15s ease',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '2px',
                        }}
                      >
                        <span style={{ fontSize: '15px' }}>{f.icon}</span>
                        <span>{f.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Alert Limit Value Input */}
              <div style={{ marginBottom: '16px' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '6px',
                  }}
                >
                  <label style={{ fontSize: '12.5px', fontWeight: 700, color: '#334155' }}>
                    Alert Threshold Limit ({unit}) <span style={{ color: '#e53e3e' }}>*</span>
                  </label>
                  {existingAlertLimit > 0 && (
                    <span style={{ fontSize: '12px', color: '#64748b' }}>
                      Current: <b style={{ color: '#0d3b5e' }}>{Number(existingAlertLimit).toFixed(2)} {unit}</b>
                    </span>
                  )}
                </div>
                <input
                  type="number"
                  step="any"
                  required
                  placeholder={`e.g. 50`}
                  value={alertLimit}
                  onChange={(e) => setAlertLimit(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#0d3b5e',
                    outline: 'none',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = '#0284c7')}
                  onBlur={(e) => (e.target.style.borderColor = '#cbd5e1')}
                />
              </div>

              {/* Recipient Email Input */}
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12.5px', fontWeight: 700, color: '#334155' }}>
                  Notification Email Address <span style={{ color: '#e53e3e' }}>*</span>
                </label>
                <input
                  type="email"
                  required
                  placeholder="e.g. planthead@company.com"
                  value={alertEmail}
                  onChange={(e) => setAlertEmail(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#0d3b5e',
                    outline: 'none',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = '#0284c7')}
                  onBlur={(e) => (e.target.style.borderColor = '#cbd5e1')}
                />
              </div>

              {/* Informative Preview Card */}
              <div
                style={{
                  marginBottom: '22px',
                  padding: '12px 14px',
                  background: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  borderRadius: '10px',
                  fontSize: '12.5px',
                  lineHeight: '1.5',
                  color: '#166534',
                }}
              >
                <div style={{ fontWeight: 700, marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <span>ℹ️</span> How this alert works:
                </div>
                <div>
                  An automatic email notification will be sent to{' '}
                  <strong>{alertEmail || 'your email'}</strong> whenever{' '}
                  <strong>
                    {alertFrequency === 'monthly'
                      ? "this month's"
                      : alertFrequency === 'weekly'
                      ? "this week's"
                      : "today's"}
                  </strong>{' '}
                  water consumption exceeds <strong>{alertLimit || '0'} {unit}</strong>.
                </div>
              </div>
            </>
          ) : isCompliance ? (
            <>
              {/* Compliance Row 1: Daily & Yearly Limits */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '12.5px', fontWeight: 700, color: '#334155' }}>
                    Daily Limit ({unit}/Day) <span style={{ color: '#e53e3e' }}>*</span>
                  </label>
                  <input
                    type="number"
                    step="any"
                    required
                    placeholder={`e.g. 86`}
                    value={dailyLimit}
                    onChange={(e) => setDailyLimit(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#0d3b5e',
                      outline: 'none',
                    }}
                    onFocus={(e) => (e.target.style.borderColor = '#0284c7')}
                    onBlur={(e) => (e.target.style.borderColor = '#cbd5e1')}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '12.5px', fontWeight: 700, color: '#334155' }}>
                    Yearly Limit ({unit}/Year)
                  </label>
                  <input
                    type="number"
                    step="any"
                    placeholder={`e.g. 27360`}
                    value={yearlyLimit}
                    onChange={(e) => setYearlyLimit(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#0d3b5e',
                      outline: 'none',
                    }}
                    onFocus={(e) => (e.target.style.borderColor = '#0284c7')}
                    onBlur={(e) => (e.target.style.borderColor = '#cbd5e1')}
                  />
                </div>
              </div>

              {/* Compliance Row 2: NOC Number */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12.5px', fontWeight: 700, color: '#334155' }}>
                  NOC Number
                </label>
                <input
                  type="text"
                  placeholder="e.g. NOC/IND/DN/2024/1695/R-2/2"
                  value={nocNumber}
                  onChange={(e) => setNocNumber(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#0d3b5e',
                    outline: 'none',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = '#0284c7')}
                  onBlur={(e) => (e.target.style.borderColor = '#cbd5e1')}
                />
              </div>

              {/* Compliance Row 3: Valid From & Valid Up To Dates */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '18px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '12.5px', fontWeight: 700, color: '#334155' }}>
                    Valid From Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '9px 12px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      fontSize: '13.5px',
                      fontWeight: 600,
                      color: '#0d3b5e',
                      outline: 'none',
                      colorScheme: 'light',
                    }}
                    onFocus={(e) => (e.target.style.borderColor = '#0284c7')}
                    onBlur={(e) => (e.target.style.borderColor = '#cbd5e1')}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '12.5px', fontWeight: 700, color: '#334155' }}>
                    Valid Up To Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '9px 12px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      fontSize: '13.5px',
                      fontWeight: 600,
                      color: '#0d3b5e',
                      outline: 'none',
                      colorScheme: 'light',
                    }}
                    onFocus={(e) => (e.target.style.borderColor = '#0284c7')}
                    onBlur={(e) => (e.target.style.borderColor = '#cbd5e1')}
                  />
                </div>
              </div>

              {/* Compliance Row 4: Upload NOC PDF */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12.5px', fontWeight: 700, color: '#334155' }}>
                  Upload NOC Document (.pdf)
                </label>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".pdf,application/pdf"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <div
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  style={{
                    border: '2px dashed #cbd5e1',
                    borderRadius: '10px',
                    padding: '16px 18px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: selectedFile ? '#f0fdf4' : '#f8fafc',
                    borderColor: selectedFile ? '#22c55e' : '#cbd5e1',
                    transition: 'all .2s ease',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#0284c7')}
                  onMouseLeave={(e) => {
                    if (!selectedFile) e.currentTarget.style.borderColor = '#cbd5e1';
                  }}
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={selectedFile ? '#16a34a' : '#64748b'} strokeWidth="1.8" style={{ margin: '0 auto 6px' }}>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <path d="M14 2v6h6" />
                    <path d="M12 18v-6" />
                    <path d="M9 15l3-3 3 3" />
                  </svg>
                  <div style={{ fontSize: '13.5px', fontWeight: 700, color: selectedFile ? '#15803d' : '#0d3b5e' }}>
                    {selectedFile
                      ? `Selected: ${selectedFile.name}`
                      : existingNocDetails?.fileName
                      ? `Attached: ${existingNocDetails.fileName} (Click to replace)`
                      : 'Click or drag NOC PDF file here (Max 15MB)'}
                  </div>
                  <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '2px' }}>
                    Official CGWA / SPCB Ground Water Clearance Certificate
                  </div>
                </div>
                {fileError && (
                  <div style={{ color: '#e53e3e', fontSize: '12px', marginTop: '5px', fontWeight: 600 }}>
                    {fileError}
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Internal Limit Single Field */
            <div style={{ marginBottom: '24px' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '8px',
                  fontSize: '13px',
                  fontWeight: 700,
                }}
              >
                <label style={{ color: '#4b5563' }}>Enter Internal Limit ({unit})</label>
                <span style={{ color: '#4b5563', fontSize: '13px' }}>
                  Existing Limit ({unit}): <b style={{ color: '#0d3b5e' }}>{Number(existingLimit).toFixed(2)}</b>
                </span>
              </div>
              <input
                type="number"
                step="any"
                required
                placeholder={`Enter Internal Limit (${unit})`}
                value={dailyLimit}
                onChange={(e) => setDailyLimit(e.target.value)}
                style={{
                  width: '100%',
                  padding: '11px 14px',
                  borderRadius: '10px',
                  border: '1px solid #d1d5db',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#0d3b5e',
                  outline: 'none',
                  transition: 'border-color .15s',
                }}
                onFocus={(e) => (e.target.style.borderColor = '#1072bb')}
                onBlur={(e) => (e.target.style.borderColor = '#d1d5db')}
              />
            </div>
          )}

          {/* Footer Action Buttons */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              alignItems: 'center',
              borderTop: '1px solid #f1f5f9',
              paddingTop: '16px',
            }}
          >
            <button
              type="button"
              disabled={isSubmitting}
              onClick={onClose}
              style={{
                padding: '9px 22px',
                borderRadius: '9px',
                border: '1px solid #d1d5db',
                background: '#ffffff',
                color: '#0d3b5e',
                fontSize: '13.5px',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'background .15s',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                padding: '9px 28px',
                borderRadius: '9px',
                border: 'none',
                background: isAlert ? '#0284c7' : isCompliance ? '#0284c7' : '#f0b429',
                color: isAlert ? '#ffffff' : isCompliance ? '#ffffff' : '#0d3b5e',
                fontSize: '13.5px',
                fontWeight: 800,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                boxShadow: isAlert
                  ? '0 4px 12px -2px rgba(2, 132, 199, 0.45)'
                  : isCompliance
                  ? '0 4px 12px -2px rgba(2, 132, 199, 0.45)'
                  : '0 4px 12px -2px rgba(240, 180, 41, 0.45)',
                transition: 'background .15s, transform .1s',
                opacity: isSubmitting ? 0.7 : 1,
              }}
            >
              {isSubmitting
                ? 'Saving...'
                : isAlert
                ? 'Save Alert Limit'
                : isCompliance
                ? 'Save NOC & Limit'
                : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
